<template>
    <div class="relative w-full flex flex-col overflow-hidden" ref="viewer_page" style="height: 100vh;"
        :data-viewer-loading="String(isLoading)"
        :data-viewer-ready="String(!isLoading && !showMsgWithoutDesign)"
        :data-viewer-has-design="String(!showMsgWithoutDesign)"
        :data-viewer-screenshot-ready="String(screenshot !== null)"
        :data-viewer-screenshot-error="String(screenshotError !== null)"
        :data-viewer-save-status-screenshot="saveStatusScreenshot"
        :data-viewer-pictogram-count="String(pictogramsInPowerPlant.length)"
    >
        <ViewerHeader v-if="_AllowedAction('show_header')"
          :show-areas="showAreas"
          :pictogram-type="pictogramType"
          :can-actions="canActionsPictogram"
          @epc-open-panel="_HandleOpenPanel"
          @epc-in-design-mode="_HandleInDesignMode"
          @epc-click-return="_HandleClickReturn"
          @epc-click-screenshot="_HandleScreenshot"/>

        <div v-if="designMode"
          class="p-2 text-sm text-gray-500 bg-white">
          <div class="h-9 flex items-center gap-4">
            <span>Vista en modo diseño</span>

            <template v-if="isDrawing">
              <span class="text-xs text-gray-400">|</span>

              <div class="flex items-center gap-1">
                <span class="text-xs">{{ $t('line') }}:</span>
                <b-select v-model="drawingStrokeWidth" size="is-small" style="width: 75px;">
                  <option :value="0.3">0.3</option>
                  <option :value="0.5">0.5</option>
                  <option :value="1">1</option>
                  <option :value="1.5">1.5</option>
                  <option :value="2">2</option>
                  <option :value="3">3</option>
                </b-select>
              </div>

              <div class="flex items-center gap-1">
                <span class="text-xs">{{ $t('type') }}:</span>
                <b-select v-model="drawingDashType" size="is-small" style="width: 100px;">
                  <option value="solid">{{ $t('solid') }}</option>
                  <option value="dashed">{{ $t('dashed') }}</option>
                  <option value="dotted">{{ $t('dotted') }}</option>
                </b-select>
              </div>

              <div class="flex items-center gap-1 ml-2">
                <b-button size="is-small" type="is-danger" outlined @click="_CancelDrawing()">
                  <b-icon icon="times" pack="fas" size="is-small"></b-icon> {{ $t('cancel') }}
                </b-button>
              </div>
            </template>

            <template v-if="selectedTool === 'pencil'">
              <span class="text-xs text-gray-400">|</span>
              <span class="text-xs"><b-icon icon="font" pack="fas" size="is-small"></b-icon> {{ $t('insert_text_hint') }}</span>
              <div class="flex items-center gap-1 ml-2">
                <b-button size="is-small" type="is-danger" outlined @click="_CancelTextTool()">
                  <b-icon icon="times" pack="fas" size="is-small"></b-icon> {{ $t('cancel') }}
                </b-button>
              </div>
            </template>

            <template v-if="editingBlock">
              <span class="text-xs text-gray-400">|</span>
              <span class="text-xs"><b-icon icon="pen-nib" pack="fas" size="is-small"></b-icon> Vector</span>
              <div class="flex items-center gap-1">
                <span class="text-xs">{{ $t('line') }}:</span>
                <b-select v-model="editingBlockStrokeWidth" size="is-small" style="width: 75px;">
                  <option :value="0.3">0.3</option>
                  <option :value="0.5">0.5</option>
                  <option :value="1">1</option>
                  <option :value="1.5">1.5</option>
                  <option :value="2">2</option>
                  <option :value="3">3</option>
                </b-select>
              </div>
              <div class="flex items-center gap-1">
                <span class="text-xs">{{ $t('type') }}:</span>
                <b-select v-model="editingBlockDashType" size="is-small" style="width: 100px;">
                  <option value="solid">{{ $t('solid') }}</option>
                  <option value="dashed">{{ $t('dashed') }}</option>
                  <option value="dotted">{{ $t('dotted') }}</option>
                </b-select>
              </div>
              <div class="flex items-center gap-1">
                <b-button size="is-small"
                  :type="editingBlockFilled ? 'is-info' : 'is-light'"
                  @click="editingBlockFilled = !editingBlockFilled">
                  <b-icon icon="fill-drip" pack="fas" size="is-small"></b-icon>
                </b-button>
              </div>
              <div class="flex items-center gap-1 ml-2">
                <b-button size="is-small" type="is-danger" outlined
                  @click="_DeleteSelectedBlock">
                  <b-icon icon="trash" pack="fas" size="is-small"></b-icon>
                </b-button>
              </div>
            </template>

            <template v-if="editingTxt">
              <span class="text-xs text-gray-400">|</span>

              <div class="flex items-center gap-1">
                <span class="text-xs">{{ $t('size') }}:</span>
                <b-select v-model="editingTxtFontSize" size="is-small" style="width: 70px;">
                  <option v-for="s in [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64]" :key="s" :value="s">{{ s }}</option>
                </b-select>
              </div>

              <div class="flex items-center gap-1">
                <b-button size="is-small"
                  :type="editingTxtBold ? 'is-info' : 'is-light'"
                  @click="editingTxtBold = !editingTxtBold">
                  <b-icon icon="bold" pack="fas" size="is-small"></b-icon>
                </b-button>
              </div>

              <span class="text-xs text-gray-400">
                <b-icon icon="redo" pack="fas" size="is-small"></b-icon> {{ $t('rotate_hint') }}
              </span>
              <div class="flex items-center gap-1 ml-2">
                <b-button size="is-small" type="is-danger" outlined
                  @click="_DeleteSelectedTxt">
                  <b-icon icon="trash" pack="fas" size="is-small"></b-icon>
                </b-button>
              </div>
            </template>
          </div>
        </div>

        <div class="relative w-full flex flex-1 min-h-0">

          <ViewerPanelPictograms v-if="showPanelViews"
              :pictograms="pictograms"
              :selected-pictograms="selectedPictograms"
              @epc-update:selected-pictograms="_HandleUpdateSelectedPictograms"
              @epc-view-selected="_OnClickDisplayLayerPictogram"
              @epc-close="(e) => { showPanelViews = !e }" />

          <div class="relative w-full flex overflow-hidden min-h-0">
            <ViewerDesign ref="viewer"
              :pictograms="selectedPictograms"
              :entity-id="entityId"
              :entity-type="entityType"
              :workLayer="workLayer"
              :canActionsPictogram="canActionsPictogram"
              :show-layer-completed="showLayerCompleted"
              :design-mode="designMode"
              :pictogram-type="pictogramType"
              :show-areas="showAreas"
              :is-drawing="isDrawing"
              :selected-tool="selectedTool"
              :drawing-stroke-width="drawingStrokeWidth"
              :drawing-dash="drawingDash"
              @epc-loaded="_OnLoaded"
              @epc-mounted="_OnMounted"
              @epc-cleared="_OnCleared"
              @epc-message="_OnMessage"
              @epc-pointerdown="_onPointerDown"
              @epc-changestatuses="_OnShowChangeStatuses"
              @epc-open-split="_OnShowFormSplit"
              @epc-isloading="(e) => { isLoading = e }"
              @epc-reload="() => { $emit('epc-reload') }"
              @epc-saving="(e) => { isLoading = e.loading; customLoaderText = e.text }"
              @epc-screenshot="(e) => { screenshot = e }"
              @epc-pictogram-dataset="_HandlePictogramDataset"
              @epc-viewer-design-mode-drawing="(e) => { isDrawing = e }"
              @epc-viewer-design-selected-tool="(e) => { selectedTool = e }"
              @epc-txt-editing="_HandleTxtEditing"
              @epc-block-editing="_HandleBlockEditing"
              @epc-selected-count="(e) => { selectedCount = e }"
            />
          </div>

          <ViewerPanelScreenshot v-if="showPanelScreenshot"
            ref="refScreenshot"
            :workLayer="displayPictogramLayer"
            :screenshot="screenshot"
            @epc-save-screenshot="_HandleSaveScreenshot"
            @epc-close="(e) => { showPanelScreenshot = !e; screenshot = null }"/>

          <LayersToolStatuses v-show="showLayerToolStatuses"
            :selected-entities="selectedEntities"
            :statuses="statuses"
            @epc-konva-changestatus="_HandleChangeStatus"
            @epc-close="(close) => showLayerToolStatuses = !close" />

          <LayersListKonva v-show="showLayerPanel"
            :layers="filterLayers"
            @toggleLayer="_OnToggleLayer"
            @toggleAll="_OnToggleAll"
            @epc-close="(close) => showLayerPanel = !close"/>

          <LayersToolSplit v-show="showLayerToolSplit"
            :selected-entities="selectedEntities"
            :statuses="statuses"
            @epc-konva-split="handleSplitEntities"
            @epc-close="(close) => showLayerToolSplit = !close" />

        </div>

        <div v-if="showMsgWithoutDesign"
          class="absolute flex items-center w-full" style="top: 50%; left: 50%; transform: translate(-50%, -50%);">
          <div class="" style="width: 480px; padding: 30px 0 8px; margin: 0 auto; color: #0a131c">
            <h3 class="font-light text-center text-4xl">Sin parque fotovoltaico</h3>
            <p class="text-center text-sm mt-2.5 mx-0 mb-9">El proyecto tiene pictogramas asociados a actividades pero no cuenta con el diseño.</p>
            <div class="text-center mt-6" style="color: #76767D">
              <b-icon icon="ban" pack="fas" custom-size="fa-5x"></b-icon>
            </div>
          </div>
        </div>

        <ViewerFooterDesign v-if="!showMsgWithoutDesign"
          :is-draggable="isDraggable"
          :show-save="_AllowedAction('save') && designMode"
          :name_vista="$t(pictogramType)"
          :selectedCount="selectedCount"
          @epc-click-pan="_OnClickPan"
          @epc-click-fit="_OnFitContent"
          @epc-click-zoom-in="_OnClickZoom"
          @epc-click-zoom-out="_OnClickZoom"
          @epc-click-progress="_OnClickProgress"
          @epc-click-fullscreen="_OnClickFullScreen"
          @epc-click-save="_HandleClickSave"
        />

        <div class="docking-panel layers-panel" style="display: none;">
          <div class="docking-panel-title docking-panel-delimiter-shadow">Capas</div>
          <div class="docking-panel-close"></div>
          <div class="docking-panel-footer">
            <div class="docking-panel-footer-resizer"></div>
          </div>
        </div>

        <ViewerSpinnerLoader ref="refSpinnerLoader" :is-loading="isLoading" :custom-loader-text="customLoaderText"/>

        <ViewerInsertPictogramsModal ref="refModalInsertPictograms"
          @epc-update:selected-pictograms="_HandleUpdateSelectedPictograms"
        />
      </div>
</template>

<script>
import ViewerDesign from "./ViewerDesign"
import Vue from "vue"
import LayersListKonva from "../viewer-konva/LayersListKonva"
import LayersToolStatuses from "../viewer-konva/LayersToolStatuses.vue"
import LayersToolSplit from "../viewer-konva/LayersToolSplit.vue"
import ViewerHeader from "./ViewerHeaderDesign.vue"
import ViewerPanelPictograms from "./ViewerPanelPictograms.vue"
import ViewerPanelComments from "../viewer-konva/ViewerPanelComments.vue"
import ViewerSpinnerLoader from "../viewer-konva/ViewerSpinnerLoader.vue"
import ViewerInsertPictogramsModal from "./ViewerInsertPictogramsModal.vue"
import ViewerFooterDesign from "./ViewerFooterDesign.vue"
import ViewerPanelScreenshot from "../viewer-pixi/ViewerPanelScreenshot.vue"

import { isFullScreenSupported, requestFullScreen, exitFullScreen } from "../shared/helpers/ui.helpers"

export default {
    name: "ViewerPageDesign",

    components: {
    ViewerDesign,
    LayersListKonva,
    LayersToolStatuses,
    LayersToolSplit,
    ViewerHeader,
    ViewerPanelPictograms,
    ViewerPanelComments,
    ViewerSpinnerLoader,
    ViewerInsertPictogramsModal,
    ViewerFooterDesign,
    ViewerPanelScreenshot
},

    props: {
      pictograms: { type: Array, default() { return [] } },
      entityId: { type: Number, required: true },
      entityType: { type: String, default: 'project' },
      workLayer: { type: String, default: null },
      canActionsPictogram: { type:Object, default() { return {} } } // acciones que se puede realizar sobre el pictograma por defecto ninguna
    },

    data() {
      return {
        selectedPictograms: [],
        layers: null,
        filterLayers: null,
        showLayerPanel: false,
        displayPictogramLayer: '', // capa actual del pictograma que se está visualizando
        displayDefaultPictogramLayer: null, // capa por defecto del pictograma a visualizar
        fullscreenEnabled: false, // si api navegador permite full screen
        isDraggable: false,
        isDrawing: false, // Esta bandera indica si se está graficando algún shape
        selectedTool: null, // Variable que ayuda a identificar que tipo de herramiente se ha seleccionado, (Tipo String)
        drawingStrokeWidth: 0.3, // Grosor de linea para dibujo de vectores
        drawingDash: [], // Patron de linea: [] = solida, [6,3] = guiones, [2,2] = punteada
        drawingDashType: 'solid', // Tipo de linea legible: solid, dashed, dotted
        editingTxt: null, // Datos del texto en edicion { fontSize, fontStyle }
        editingTxtFontSize: 16,
        editingTxtBold: false,
        editingBlock: null, // Datos del bloque en edicion { strokeWidth, dash, fill }
        editingBlockStrokeWidth: 0.3,
        editingBlockDashType: 'solid',
        editingBlockFilled: true,

        showLayerToolStatuses: false,
        selectedEntities: [], // entidades seleccionadas para cambiar de estado enviado desde konvaviewer.vue
        statuses: [], // estados que se aplican en una capa enviado desde konvaviewer.vue

        showLayerToolSplit: false,

        showPanelViews: false,
        showLayerCompleted: false, // visualiza una capa representando las estructuras que tienen estado completado por defecto false


        isLoading: true, // Indica si esta visible el loader (inicia en true para mostrar desde el principio)
        customLoaderText: null, // texto mostrado en el loader

        designMode: false, // indica si se esta en modo diseño o en modo visualización de datos
        datasetProject: true,
        pictogramIdSelected: 0,
        showProgress: false,
        showMsgWithoutDesign: false, // bandera que permite mostrar un mensaje si no existe el diseño

        pictogramsInPowerPlant: [], // Esta variable contiene solo los Id de los pictogramas que estan en el diseño (power plant) es obtenido cuando se carga la data del power plant y los que se van añadiendo o quitando
        pictogramType: 'structures', // Bandera que determina el tipo de pictogramas que se deben cargar, por defecto son las structures
        showAreas: false, // bandera para mostrar las areas marcadas
        screenshot: null, // captura de pantalla generada por el visor
        screenshotError: null, // error al generar la captura (null = sin error)
        showPanelScreenshot: false, // bandera para mostrar el panel de captura
        selectedCount: 0, // conteo de elementos seleccionados en el visor
        saveStatusScreenshot: 'idle' // estado del guardado de screenshot: idle | saving | success | error
      }
    },

    computed: {

    },

    watch: {
      displayPictogramLayer(newVal, oldVal) {
        this.showLayerCompleted = newVal == 'modules'
      },

      drawingDashType(type) {
        const dashPatterns = {
          solid: [],
          dashed: [6, 3],
          dotted: [2, 2]
        }
        this.drawingDash = dashPatterns[type] || []
      },

      editingTxtFontSize(size) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtFontSize(Number(size))
        }
      },

      editingTxtBold(bold) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtBold(bold)
        }
      },

      editingBlockStrokeWidth(width) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockStrokeWidth(Number(width))
        }
      },

      editingBlockDashType(type) {
        if (this.editingBlock) {
          const dashPatterns = { solid: [], dashed: [6, 3], dotted: [2, 2] }
          this.$refs.viewer.UpdateSelectedBlockDash(dashPatterns[type] || [])
        }
      },

      editingBlockFilled(filled) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockFill(filled)
        }
      }
    },

    mounted() {

    },

    methods: {
      _HandlePictogramDataset(pictogramId) {
        this.$emit('epc-pictogram-project-dataset', false)
        this.datasetProject = false
        this.pictogramIdSelected = parseInt(pictogramId)
      },

      _HandleClickReturn() {
        this.$router.go(-1)
      },

      _HandleInDesignMode(e) {
        this.designMode = e
        if (!e)
          this.showPanelViews = false
        this.$emit('epc-in-design-mode', e)
      },

      ShowLoader(data) {
        this.isLoading = data.loading;
        this.customLoaderText = data.text
      },

      _OnLoaded(e) {
          const layers = this.$refs.viewer.GetLayers()
          layers.forEach(lyr => Vue.set(lyr, "isVisible", true))
          this.layers = layers
          this.showProgress = e.length > 0
          this.showMsgWithoutDesign = e.length == 0
          this.pictogramsInPowerPlant = e

          this.$emit('epc-photovoltaic-park-loaded', e)
      },

      _OnMounted() {
        // this._FilterLayer()
        // this._OnClickDisplayLayerPictogram(this.displayPictogramLayer)
      },

      _FilterLayer() {
        // Primero filtramos a queyos que en el name layer tiene el prifijo el codigo del layer a visualizar
        // luego verificamos si esto devuelve un resultado, caso contrario se filtrar a aquellos que no contengan
        // EPC en el nombre
        let filterType = this.displayPictogramLayer.toUpperCase()
        this.filterLayers = this.layers.filter( (l) => l.name.indexOf(filterType) !== -1 )

        if( this.filterLayers.length == 0 )
          this.filterLayers = this.layers.filter( (l) => l.name.indexOf('EPC') === -1 )
      },

      _OnCleared() {
          this.layers = null
      },

      _OnToggleLayer(layer, newState) {
          layer.isVisible = newState
          this.$refs.viewer.ShowLayer(layer.name, newState)
      },

      _OnToggleAll(newState) {
          if (this.layers) {
              for (const layer of this.layers) {
                  if (layer.isVisible !== newState) {
                      this._OnToggleLayer(layer, newState)
                  }
              }
          }
      },

      _OnMessage(e) {
          let type = "info"
          // switch (e.detail.level) {
          // case _DxfViewer.MessageLevel.WARN:
          //     type = "warning"
          //     break
          // case _DxfViewer.MessageLevel.ERROR:
          //     type = "negative"
          //     break
          // }
          // this.$q.notify({ type, message: e.detail.message })
      },

      _OnClickDisplayLayerPictogram(type) {
        if (type === this.displayPictogramLayer)
          this.displayPictogramLayer = this.displayDefaultPictogramLayer
        else
          this.displayPictogramLayer = type

        // mandamos a renderizar los layers filtrados por tipo
        this._FilterLayer()
        // mandamos a renderizar
        this.$refs.viewer.Render(this.displayPictogramLayer)
      },

      _onPointerDown(e) {
        console.log('_onPointerDown up: ', e.detail)
        this.$refs.viewer.GetViewer().ResaltarObject(e.detail.domEvent)
      },

      _OnClickZoom(zoomIn) {
        if( zoomIn )
          this.$refs.viewer.ZoomIn()
        else
          this.$refs.viewer.ZoomOut()
      },

      _OnClickFullScreen() {
        if (this.fullscreenEnabled) {
          exitFullScreen();
          this.fullscreenEnabled = false;
        } else {
          if (isFullScreenSupported()) {
            requestFullScreen(this.$refs.viewer_page);
            this.fullscreenEnabled = true;
          }
        }
      },

      _OnFitContent() {
        this.$refs.viewer.FitToScreen()
      },

      async _HandleClickSave() {
        this.$refs.viewer.Save()
      },

      _OnClickPan() {
        this.isDraggable = !this.isDraggable
        this.$refs.viewer.EnableDraggable(this.isDraggable)
      },

      _OnClickProgress() {
        this.datasetProject = true
        this.$emit('epc-pictogram-project-dataset', true)
      },

      _OnShowChangeStatuses(e) {
        this.showLayerToolStatuses = e.show
        this.selectedEntities = e.selected_entities
        this.statuses = e.statuses
      },

      _OnShowFormSplit(e) {
        this.showLayerToolSplit = e.show
        this.selectedEntities = e.selected_entities
      },

      _HandleChangeStatus(e) {
        // de momento una vez que cambia de estado solo completamos la acción pasando los datos a la variable que
        // contiene todos los elementos que han cambiado de estado (refierase a la estructura general no a la propiedad)
        this.$refs.viewer.CompleteAction()
      },

      handleSplitEntities(e) {
        this.$refs.viewer.SplitEntities(e)
      },

      _CancelDrawing() {
        this.isDrawing = false
        this.selectedTool = null
      },

      _CancelTextTool() {
        this.selectedTool = null
      },

      _HandleTxtEditing(data) {
        this.editingTxt = data
        if (data) {
          this.editingTxtFontSize = data.fontSize || 16
          this.editingTxtBold = data.fontStyle === 'bold'
        }
      },

      _DeleteSelectedBlock() {
        this.$refs.viewer.DeleteSelectedBlock()
        this.editingBlock = null
      },

      _DeleteSelectedTxt() {
        this.$refs.viewer.DeleteSelectedTxt()
        this.editingTxt = null
      },

      _HandleBlockEditing(data) {
        this.editingBlock = data
        if (data) {
          this.editingBlockStrokeWidth = data.strokeWidth || 0.3
          // Determinar tipo de dash
          const dash = data.dash
          if (!dash || dash.length === 0) this.editingBlockDashType = 'solid'
          else if (dash[0] >= 4) this.editingBlockDashType = 'dashed'
          else this.editingBlockDashType = 'dotted'
          // Determinar si tiene relleno
          this.editingBlockFilled = data.hasFill
        }
      },

      _HandleOpenPanel(namePanel) {
        if ( namePanel === 'panel-views' )
          this.$refs.refModalInsertPictograms.show(this.pictograms, this.pictogramsInPowerPlant)

        if ( namePanel === 'panel-drawing' ) {
          this.isDrawing = true
        }

        if ( namePanel === 'view-pilings' || namePanel === 'view-structures' || namePanel === 'view-modules' ) {
          this.pictogramType = namePanel.replace('view-', '');
          this.showAreas = false
        }

        if ( namePanel === 'view-areas' ) {
          // this.pictogramType = namePanel.replace('view-', '');
          this.showAreas = !this.showAreas
        }

        if (namePanel === 'tool-pencil') {
          this.selectedTool = 'pencil'
        }

        // if (namePanel === 'panel-views')
        //   this.showPanelViews = true

        // if (namePanel === 'panel-layers')
        //   this.showLayerPanel = true
      },

      _AllowedAction(action) {
        if (Object.keys(this.canActionsPictogram).length === 0)
          return false;

        return this.canActionsPictogram[action] === 'undefined' ? false : this.canActionsPictogram[action]
      },

      async _HandleScreenshot() {
        try {
          this.screenshotError = null
          this.isLoading = true
          this.customLoaderText = this.$t('screenshot')
          await this.$nextTick()
          await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))

          let img = this.$refs.viewer.konvaViewer.Screenshot({ includeOverlays: this.showAreas })
          // CropImage: deshabilitado porque CalculateBoundingBox ya recorta al contenido con padding,
          // el escaneo pixel-por-pixel de CropImage es redundante y costoso en imágenes grandes
          // img = await this.$refs.viewer.konvaViewer.CropImage(img)
          img = await this.$refs.viewer.konvaViewer.GetBase64Image(img, true)

          this.screenshot = img
          this.showPanelScreenshot = true
        } catch (error) {
          console.error('Error capturing screenshot:', error)
          this.screenshotError = error.message || 'unknown_error'
          this.$notify.error('error_saving')
        } finally {
          this.isLoading = false
          this.customLoaderText = null
        }
      },

      async _HandleSaveScreenshot() {
        this.saveStatusScreenshot = 'saving'
        const success = await this.$refs.viewer.SaveScreenshot(this.screenshot)
        this.saveStatusScreenshot = success ? 'success' : 'error'
      },

      _HandleUpdateSelectedPictograms(e) {
        // La variable e es un array que tiene los id de los pictogramas a incluir en el diseño
        this.showMsgWithoutDesign = e.length == 0

        let newSelected = []
        this.pictograms.forEach(item => {
          if (e.includes(item.id)) {
            // let exist = this.selectedPictograms.find(p => p.id == item.id)
            // if (! exist) {
              newSelected.push(item)
            // }
          }
        })

        this.selectedPictograms = newSelected
      }
    },

    created() {
      const validTypes = ['structures', 'pilings', 'modules']
      if (this.workLayer && validTypes.includes(this.workLayer)) {
        this.pictogramType = this.workLayer
      }
    },
}
</script>

<style scoped>

/* Ajustar el canvas Konva para que use 100% de altura */
:deep(.canvasContainer) {
  height: 100% !important;
}

/* Asegurar que el contenedor principal no cause scroll */
.relative.w-full {
  max-height: calc(100vh - 6rem);
  overflow: hidden;
}

.docking-panel {
  box-shadow: 1px 3px 10px 0 rgba(0,0,0,.4);
  -webkit-touch-callout: none;
  background: transparent;
  border: none;
  border-radius: 5px;
  overflow-x: hidden;
  overflow-y: hidden;
  position: absolute;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  z-index: 2;
}

.docking-panel-title {
  background-color: rgba(34,34,34,.94);
  border-bottom: 1px solid rgba(34,34,34,.8);
  color: #fff;

  border: none;
  border-radius: 5px 5px 0 0;
  cursor: move;
  font-size: 20px;
  font-weight: 300;
  line-height: 27px;
  overflow: hidden;
  padding: 11px 0 11px 14px;
  position: relative;
  resize: none;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
  z-index: 1;
}

.docking-panel-delimiter-shadow {
  box-shadow: 0 3px 5px 0 rgba(0,0,0,.1);
}

.docking-panel-close {
  background-position: 0 19px;
  background-repeat: no-repeat;
  cursor: pointer;
  height: 50px;
  position: absolute;
  right: 0;
  top: 0;
  width: 25px;
  z-index: 1;
}

.docking-panel-close {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTEiIGhlaWdodD0iMTEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNLjQ1NSAxMC45OTdhLjUuNSAwIDAgMS0uMzEtLjg1bDEwLTEwYS41MDIuNTAyIDAgMCAxIC43MS43MWwtMTAgMTBhLjUuNSAwIDAgMS0uNC4xNFoiIGZpbGw9IiM5NDk0OUYiLz48cGF0aCBkPSJNMTAuNDU3IDExLjAwN2EuNS41IDAgMCAxLS4zMS0uMTVsLTEwLTEwYS41MDIuNTAyIDAgMCAxIC43MS0uNzFsMTAgMTBhLjUuNSAwIDAgMS0uNC44NloiIGZpbGw9IiM5MzlDQTUiLz48L2c+PC9zdmc+);
}

.docking-panel-footer {
  background-color: #222;
}

.docking-panel-footer {
  bottom: 0;
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  height: 20px;
  left: 0;
  position: absolute;
  width: 100%;
}

.docking-panel-footer-resizer {
  background-position-x: 9px;
  background-position-y: 8px;
  background-repeat: no-repeat;
  bottom: 0;
  cursor: pointer;
  height: 20px;
  position: absolute;
  right: 0;
  width: 20px;
}

.docking-panel-footer-resizer {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI3IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLWxpbmVjYXA9InNxdWFyZSI+PHBhdGggZD0ibS41IDYuNSA2LTZNNC41IDYuNWwxLjUzNi0xLjUzNiIgc3Ryb2tlPSIjMDAwIi8+PHBhdGggZD0ibTEuNSA2LjUgNi02TTUuNSA2LjVsMS41MzYtMS41MzYiIHN0cm9rZT0iI0JFQzhEMiIvPjwvZz48L3N2Zz4=);
  background-position-x: 9px;
  background-position-y: 8px;
  background-repeat: no-repeat;
}

.layers-panel {
  border-color: transparent;
  height: 250px;
  left: 220px;
  min-height: 100px;
  min-width: 250px;
  top: 180px;
  width: 300px;
}

.pressed {
  background-color: rgb(51, 85, 136);
  color: white;
}

.not-pressed {
  background-color: rgba(85, 119, 187, 0.133);
  color: rgb(51, 85, 136);
}

</style>
